/**
 * IndexedDB Database for Local Storage
 *
 * This provides offline-first storage for NIDOnote.
 * Data is cached locally and synced to Google Drive.
 *
 * Architecture:
 * - IndexedDB (via Dexie) for local caching
 * - Google Drive for persistent storage
 * - Sync engine reconciles between local and Drive
 */

import Dexie, { type Table } from 'dexie';

/**
 * Unified Content Item
 *
 * This replaces separate Note and CalendarEvent types with a single flexible structure.
 * Different space types configure which fields are shown/used via SpaceFeatures.
 */
export interface ContentItem {
  // Core fields (always present)
  id: string;
  spaceId: string;
  title: string;
  content?: string; // Rich text body
  createdAt: string; // ISO 8601
  updatedAt: string;
  createdBy: string; // User ID
  lastEditedBy: string; // User ID

  // Completion tracking (for to-dos, groceries, etc.)
  completed?: boolean;
  completedAt?: string;
  completedBy?: string;

  // Date/time fields (for events, tasks with deadlines)
  dueDate?: string; // ISO 8601
  startDate?: string; // For events
  endDate?: string;
  isAllDay?: boolean;

  // Organization
  tags?: string[]; // Categories, labels, etc.
  priority?: 'low' | 'medium' | 'high';
  isPinned?: boolean;
  isArchived?: boolean;
  order?: number; // Manual ordering

  // Structured metadata (space-specific custom fields)
  metadata?: Record<string, any>; // Quantity, location, URL, etc.

  // Relations
  parentId?: string; // For sub-items (recipe ingredients, subtasks)

  // Sync
  driveFileId?: string; // Google Drive file ID
  syncStatus: 'synced' | 'pending' | 'error';
  syncError?: string;

  // Google Calendar integration (for event-type items)
  syncToGoogleCalendar?: boolean;
  googleCalendarEventId?: string | null;
  googleCalendarSyncStatus?: 'pending' | 'synced' | 'error' | null;
  googleCalendarSyncError?: string | null;
  lastGoogleCalendarSync?: string | null;
}

// Note interface (DEPRECATED - kept for backward compatibility during migration)
export interface Note {
  id: string;
  spaceId: string;
  title: string;
  content: string | any; // Plain text or Tiptap JSON (JSONContent)
  contentFormat?: 'plaintext' | 'tiptap-json'; // Format indicator
  tags: string[];
  category?: string | null; // Category ID
  isPinned: boolean;
  isArchived: boolean;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  createdBy: string; // User ID
  lastEditedBy: string; // User ID
  driveFileId?: string; // Google Drive file ID (for sync)
  syncStatus: 'synced' | 'pending' | 'error';
  syncError?: string;
}

// Calendar Event interface
export interface CalendarEvent {
  id: string;
  spaceId: string;
  title: string;
  description?: string;
  startTime: string; // ISO 8601
  endTime: string; // ISO 8601
  isAllDay: boolean;
  location?: string;
  color?: string;
  category?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  rsvpEnabled: boolean;
  rsvpList: RSVP[];
  status: 'confirmed' | 'tentative' | 'cancelled';
  visibility: 'public' | 'private' | 'family';
  driveFileId?: string;
  syncStatus: 'synced' | 'pending' | 'error';
  syncError?: string;

  // Google Calendar integration
  syncToGoogleCalendar: boolean; // User toggle: sync this event?
  googleCalendarEventId?: string | null; // Google Calendar event ID
  googleCalendarSyncStatus?: 'pending' | 'synced' | 'error' | null;
  googleCalendarSyncError?: string | null;
  lastGoogleCalendarSync?: string | null; // ISO timestamp
}

// RSVP interface
export interface RSVP {
  userId: string;
  email: string;
  name: string;
  status: 'yes' | 'no' | 'maybe' | 'pending';
  guestCount: number;
  note?: string;
  respondedAt: string;
}

// Shared Space interface
export interface SharedSpace {
  id: string;
  name: string;
  type: string; // Template ID: 'notes', 'todos', 'groceries', 'custom', etc.
  customLabel?: string; // If type='custom', user's label
  description?: string;
  color?: string;
  icon?: string;
  managerId: string; // User ID
  members: SpaceMember[];
  settings: SpaceSettings;
  features: SpaceFeatures; // Feature configuration for this space
  createdAt: string;
  updatedAt: string;
  driveFolderId?: string; // Root folder in Drive

  // Google Calendar Integration (for shared spaces)
  googleCalendarId?: string;              // Google Calendar ID for this space
  googleCalendarSyncEnabled: boolean;     // Whether calendar sync is enabled
  googleCalendarColor?: string;           // Calendar color in Google Calendar

  syncStatus: 'synced' | 'pending' | 'error';
}

// Space Features interface (imported from templates)
export interface SpaceFeatures {
  showCompletionCheckbox: boolean;
  showDueDate: boolean;
  showPriority: boolean;
  showTags: boolean;
  defaultView: 'list' | 'grid' | 'calendar' | 'kanban';
  sortBy: 'createdAt' | 'updatedAt' | 'title' | 'dueDate' | 'priority' | 'order';
  groupBy?: 'none' | 'status' | 'priority' | 'tags' | 'dueDate';
  showCompleted: boolean;
  defaultFilters?: Record<string, any>;
  customFields?: CustomField[];
}

// Custom Field interface
export interface CustomField {
  id: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'url';
  placeholder?: string;
  options?: string[];
  required?: boolean;
  defaultValue?: any;
}

// Space Member interface
export interface SpaceMember {
  userId: string;
  email: string;
  displayName?: string;
  avatarUrl?: string;
  role: 'manager' | 'editor' | 'viewer';
  joinedAt: string;
  lastActive: string;
}

// Space Settings interface
export interface SpaceSettings {
  defaultCalendarView: 'month' | 'week' | 'day';
  allowGuests: boolean;
  sendNotifications: boolean;
  colorTheme: string;
}

// Sync Metadata interface
export interface SyncMetadata {
  key: string; // 'lastSync', 'driveFolderIds', etc.
  value: string;
  updatedAt: string;
}

// Deleted items tracking (to prevent re-downloading deleted items)
export interface DeletedItem {
  id: string; // The ID of the deleted item
  type: 'note' | 'event' | 'space' | 'contentItem';
  deletedAt: string; // ISO 8601
}

/**
 * NIDOnote Database
 */
class NIDOnoteDatabase extends Dexie {
  notes!: Table<Note, string>;
  events!: Table<CalendarEvent, string>;
  spaces!: Table<SharedSpace, string>;
  syncMetadata!: Table<SyncMetadata, string>;
  deletedItems!: Table<DeletedItem, string>;
  contentItems!: Table<ContentItem, string>;

  constructor() {
    super('NIDOnoteDB');

    // Define schema version 1
    this.version(1).stores({
      notes: 'id, spaceId, title, createdAt, updatedAt, isPinned, isArchived, syncStatus, *tags',
      events: 'id, spaceId, title, startTime, endTime, createdAt, syncStatus',
      spaces: 'id, name, managerId, createdAt',
      syncMetadata: 'key, updatedAt',
    });

    // Version 2: Add deletedItems table for tracking deletions
    this.version(2).stores({
      notes: 'id, spaceId, title, createdAt, updatedAt, isPinned, isArchived, syncStatus, *tags',
      events: 'id, spaceId, title, startTime, endTime, createdAt, syncStatus',
      spaces: 'id, name, managerId, createdAt',
      syncMetadata: 'key, updatedAt',
      deletedItems: 'id, type, deletedAt',
    });

    // Version 3: Add contentItems table for unified content model
    this.version(3).stores({
      notes: 'id, spaceId, title, createdAt, updatedAt, isPinned, isArchived, syncStatus, *tags',
      events: 'id, spaceId, title, startTime, endTime, createdAt, syncStatus',
      spaces: 'id, name, managerId, createdAt',
      syncMetadata: 'key, updatedAt',
      deletedItems: 'id, type, deletedAt',
      contentItems: 'id, spaceId, title, createdAt, updatedAt, completed, syncStatus, *tags, priority, dueDate, startDate',
    });

    // Version 4: Add rich text support with contentFormat field
    this.version(4).stores({
      notes: 'id, spaceId, title, createdAt, updatedAt, isPinned, isArchived, syncStatus, *tags, contentFormat',
      events: 'id, spaceId, title, startTime, endTime, createdAt, syncStatus',
      spaces: 'id, name, managerId, createdAt',
      syncMetadata: 'key, updatedAt',
      deletedItems: 'id, type, deletedAt',
      contentItems: 'id, spaceId, title, createdAt, updatedAt, completed, syncStatus, *tags, priority, dueDate, startDate',
    }).upgrade(tx => {
      // Mark existing notes as plain text format
      return tx.table('notes').toCollection().modify(note => {
        if (!note.contentFormat) {
          note.contentFormat = 'plaintext';
        }
      });
    });
  }
}

// Export singleton instance
export const db = new NIDOnoteDatabase();

/**
 * Database Helper Functions
 */

// Notes CRUD
export const notesDB = {
  // Get all notes for a space
  async getBySpace(spaceId: string): Promise<Note[]> {
    return db.notes.where('spaceId').equals(spaceId).reverse().sortBy('updatedAt');
  },

  // Get a single note
  async get(id: string): Promise<Note | undefined> {
    return db.notes.get(id);
  },

  // Create a note
  async create(
    note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'> & {
      id?: string;
      createdAt?: string;
      updatedAt?: string;
    }
  ): Promise<Note> {
    const now = new Date().toISOString();
    const newNote: Note = {
      ...note,
      id: note.id || crypto.randomUUID(), // Use provided ID or generate new one
      createdAt: note.createdAt || now, // Use provided timestamp or current time
      updatedAt: note.updatedAt || now, // Use provided timestamp or current time
    };

    // Use put() instead of add() to allow overwriting if ID already exists
    // This prevents duplicate notes during sync
    await db.notes.put(newNote);
    return newNote;
  },

  // Update a note
  async update(id: string, updates: Partial<Note>): Promise<Note> {
    await db.notes.update(id, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
    const updated = await db.notes.get(id);
    if (!updated) throw new Error('Note not found after update');
    return updated;
  },

  // Update a note without modifying updatedAt (for sync operations only)
  async updateSyncOnly(id: string, updates: Partial<Note>): Promise<Note> {
    await db.notes.update(id, updates);
    const updated = await db.notes.get(id);
    if (!updated) throw new Error('Note not found after update');
    return updated;
  },

  // Delete a note
  async delete(id: string): Promise<void> {
    await db.notes.delete(id);
  },

  // Search notes
  async search(spaceId: string, query: string): Promise<Note[]> {
    const allNotes = await db.notes.where('spaceId').equals(spaceId).toArray();
    const lowerQuery = query.toLowerCase();
    return allNotes.filter(
      (note) =>
        note.title.toLowerCase().includes(lowerQuery) ||
        note.content.toLowerCase().includes(lowerQuery) ||
        note.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
    );
  },

  // Get pinned notes
  async getPinned(spaceId: string): Promise<Note[]> {
    return db.notes
      .where('[spaceId+isPinned]')
      .equals([spaceId, 1])
      .reverse()
      .sortBy('updatedAt');
  },
};

// Events CRUD
export const eventsDB = {
  // Get all events for a space
  async getBySpace(spaceId: string): Promise<CalendarEvent[]> {
    return db.events.where('spaceId').equals(spaceId).sortBy('startTime');
  },

  // Get events in date range
  async getByDateRange(
    spaceId: string,
    startDate: string,
    endDate: string
  ): Promise<CalendarEvent[]> {
    return db.events
      .where('spaceId')
      .equals(spaceId)
      .and((event) => event.startTime >= startDate && event.endTime <= endDate)
      .sortBy('startTime');
  },

  // Get a single event
  async get(id: string): Promise<CalendarEvent | undefined> {
    return db.events.get(id);
  },

  // Create an event
  async create(
    event: Omit<CalendarEvent, 'id' | 'createdAt' | 'updatedAt'> & {
      id?: string;
      createdAt?: string;
      updatedAt?: string;
    }
  ): Promise<CalendarEvent> {
    const now = new Date().toISOString();
    const newEvent: CalendarEvent = {
      ...event,
      id: event.id || crypto.randomUUID(), // Use provided ID or generate new one
      createdAt: event.createdAt || now, // Use provided timestamp or current time
      updatedAt: event.updatedAt || now, // Use provided timestamp or current time
    };

    // Use put() instead of add() to allow overwriting if ID already exists
    // This prevents duplicate events during sync
    await db.events.put(newEvent);
    return newEvent;
  },

  // Update an event
  async update(id: string, updates: Partial<CalendarEvent>): Promise<CalendarEvent> {
    await db.events.update(id, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
    const updated = await db.events.get(id);
    if (!updated) throw new Error('Event not found after update');
    return updated;
  },

  // Delete an event
  async delete(id: string): Promise<void> {
    await db.events.delete(id);
  },
};

// Spaces CRUD
export const spacesDB = {
  // Get all spaces for a user
  async getAll(): Promise<SharedSpace[]> {
    return db.spaces.toArray();
  },

  // Get a single space
  async get(id: string): Promise<SharedSpace | undefined> {
    return db.spaces.get(id);
  },

  // Create a space
  async create(space: Omit<SharedSpace, 'id' | 'createdAt' | 'updatedAt'>): Promise<SharedSpace> {
    const newSpace: SharedSpace = {
      ...space,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await db.spaces.add(newSpace);
    return newSpace;
  },

  // Update a space
  async update(id: string, updates: Partial<SharedSpace>): Promise<SharedSpace> {
    await db.spaces.update(id, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
    const updated = await db.spaces.get(id);
    if (!updated) throw new Error('Space not found after update');
    return updated;
  },

  // Delete a space
  async delete(id: string): Promise<void> {
    await db.spaces.delete(id);
  },
};

// Sync Metadata helpers
export const syncMetadataDB = {
  async get(key: string): Promise<string | undefined> {
    const metadata = await db.syncMetadata.get(key);
    return metadata?.value;
  },

  async set(key: string, value: string): Promise<void> {
    await db.syncMetadata.put({
      key,
      value,
      updatedAt: new Date().toISOString(),
    });
  },

  async delete(key: string): Promise<void> {
    await db.syncMetadata.delete(key);
  },
};

// Deleted Items helpers (track deletions to prevent re-download)
export const deletedItemsDB = {
  async add(id: string, type: 'note' | 'event' | 'space' | 'contentItem'): Promise<void> {
    await db.deletedItems.put({
      id,
      type,
      deletedAt: new Date().toISOString(),
    });
  },

  async isDeleted(id: string): Promise<boolean> {
    const item = await db.deletedItems.get(id);
    return !!item;
  },

  async remove(id: string): Promise<void> {
    await db.deletedItems.delete(id);
  },

  async getAll(type?: 'note' | 'event' | 'space'): Promise<DeletedItem[]> {
    if (type) {
      return db.deletedItems.where('type').equals(type).toArray();
    }
    return db.deletedItems.toArray();
  },
};

// ContentItems CRUD (Unified Content Model)
export const contentItemsDB = {
  // Get all content items for a space
  async getBySpace(spaceId: string): Promise<ContentItem[]> {
    return db.contentItems.where('spaceId').equals(spaceId).reverse().sortBy('updatedAt');
  },

  // Get a single content item
  async get(id: string): Promise<ContentItem | undefined> {
    return db.contentItems.get(id);
  },

  // Get all content items
  async getAll(): Promise<ContentItem[]> {
    return db.contentItems.toArray();
  },

  // Create a content item
  async create(
    item: Omit<ContentItem, 'id' | 'createdAt' | 'updatedAt'> & {
      id?: string;
      createdAt?: string;
      updatedAt?: string;
    }
  ): Promise<ContentItem> {
    const now = new Date().toISOString();
    const newItem: ContentItem = {
      ...item,
      id: item.id || crypto.randomUUID(),
      createdAt: item.createdAt || now,
      updatedAt: item.updatedAt || now,
    };

    await db.contentItems.put(newItem);
    return newItem;
  },

  // Update a content item
  async update(id: string, updates: Partial<ContentItem>): Promise<ContentItem> {
    await db.contentItems.update(id, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
    const updated = await db.contentItems.get(id);
    if (!updated) throw new Error('Content item not found after update');
    return updated;
  },

  // Update without modifying updatedAt (for sync operations only)
  async updateSyncOnly(id: string, updates: Partial<ContentItem>): Promise<ContentItem> {
    await db.contentItems.update(id, updates);
    const updated = await db.contentItems.get(id);
    if (!updated) throw new Error('Content item not found after update');
    return updated;
  },

  // Delete a content item
  async delete(id: string): Promise<void> {
    await db.contentItems.delete(id);
  },

  // Bulk operations
  async bulkCreate(items: ContentItem[]): Promise<void> {
    await db.contentItems.bulkPut(items);
  },

  async bulkDelete(ids: string[]): Promise<void> {
    await db.contentItems.bulkDelete(ids);
  },

  // Query helpers
  async getCompleted(spaceId: string): Promise<ContentItem[]> {
    return db.contentItems
      .where('spaceId')
      .equals(spaceId)
      .and((item) => item.completed === true)
      .toArray();
  },

  async getPending(spaceId: string): Promise<ContentItem[]> {
    return db.contentItems
      .where('spaceId')
      .equals(spaceId)
      .and((item) => !item.completed)
      .toArray();
  },

  async getByTag(spaceId: string, tag: string): Promise<ContentItem[]> {
    return db.contentItems
      .where('spaceId')
      .equals(spaceId)
      .and((item) => item.tags?.includes(tag) || false)
      .toArray();
  },

  async getOverdue(spaceId: string): Promise<ContentItem[]> {
    const now = new Date().toISOString();
    return db.contentItems
      .where('spaceId')
      .equals(spaceId)
      .and((item) => {
        if (!item.dueDate) return false;
        if (item.completed) return false;
        return item.dueDate < now;
      })
      .toArray();
  },
};

// Initialize default space on first run
export async function initializeDefaultSpace(userId: string): Promise<SharedSpace> {
  const existingSpaces = await spacesDB.getAll();

  if (existingSpaces.length > 0) {
    return existingSpaces[0];
  }

  // Create default "Personal" space with features from 'notes' template
  const defaultSpace = await spacesDB.create({
    name: 'Personal',
    type: 'notes',
    description: 'Your personal space',
    color: '#4A90E2',
    managerId: userId,
    members: [
      {
        userId,
        email: '', // Will be filled in by caller
        role: 'manager',
        joinedAt: new Date().toISOString(),
        lastActive: new Date().toISOString(),
      },
    ],
    settings: {
      defaultCalendarView: 'month',
      allowGuests: true,
      sendNotifications: true,
      colorTheme: '#4A90E2',
    },
    features: {
      // Default 'notes' template features
      showCompletionCheckbox: false,
      showDueDate: false,
      showPriority: false,
      showTags: true,
      defaultView: 'list',
      sortBy: 'updatedAt',
      showCompleted: true,
    },
    // Calendar sync disabled by default for personal spaces (use primary calendar)
    googleCalendarSyncEnabled: false,
    syncStatus: 'pending',
  });

  return defaultSpace;
}
